import * as Pet from '../pet';
import { SB } from '@play-co/replicant';
import {
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import { getChatbotFlexMessage } from 'src/replicant/chatbot/flex/utils';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from 'src/replicant/config/scheduledActions/createScheduledActions';
import { Feature } from 'src/replicant/analytics';
import {
  deliverChatbotMessage,
  getJitterDelayInMs,
} from '../chatbot/chatbot.util';
import { getAttributeValue, getEquippedPetSpecies } from '../pet/pet.getters';
import { AlternativeOpenWebBuckets } from '../utils/payload';
import { getVisitedCount, getVisitsCount } from '../visit/visit.getters';
import * as Fate from '../fate';
import { getChatbotMessageChannel } from './chatbot.getters';
import { getStreakBattleRoyaleStatus } from '../streakbattleroyale/streakbattleroyale.getters';
import {
  LiveOpsEventType,
  liveOpsEventType,
  liveOpsSchedule,
} from './chatbot.ruleset';
import { duration } from '../utils/duration';
import * as match2TournamentGetters from '../match2/tournament/match2-tournament.getters';
import { match2TournamentRuleset } from '../match2/tournament/match2-tournament.ruleset';
import { getJSTDateTime } from '../utils/time';
import { State } from '../State';
import { isDailyChallengeAvailable } from '../match2/dailychallenge/match2-dailychallenge.getters';
import { dreamDriftIsAvailable } from '../dreamDrift/dreamDrift.getters';
import { getLocale } from '../getters/user';
import { getLocaleConfig } from '../creatives/text';
import { getOngoingWeeklyLeaderboardEvent } from '../weeklyLeaderboard/weeklyLeaderboard.getters';
import { scheduleWeeklyLeaderboardMessages } from '../weeklyLeaderboard/weeklyLeaderboard.modifiers';
import { GENERIC_AVATAR } from '../profile/assets';
import {
  GuestNotificationId,
  isValidGuestNotificationId,
  sendGuestPN,
  areGuestNotificationsEnabled,
} from '../guest/guest.chatbot';

// Adding this helper in other related files introduces a circular dependency
export function shouldSendLiveOpsMessage(
  state: State,
  eventType: LiveOpsEventType,
  now: number,
) {
  switch (eventType) {
    case 'matchLife':
    case 'waterballoon':
    case 'moveLikeBuruburu':
    case 'streakChampion':
    case 'lollipopWednesday':
    case 'yellowFestival':
    case 'miniFirecracker':
      return isDailyChallengeAvailable(state);
    case 'tournamentStart':
    case 'tournamentTomorrow':
    case 'tournamentSoon':
    case 'tournamentEnd':
      return match2TournamentGetters.canPlay(state, now);
    case 'turtleRace':
      // Retired jobs remain valid but never deliver Turtle Race content.
      return false;
    case 'dreamDrift':
      // Guests receive Dream Drift through their dedicated notification schedule.
      if (areGuestNotificationsEnabled(state)) return false;

      // Only notify players who can access the current Dream Drift event.
      return dreamDriftIsAvailable(state, now);
    case 'lampsStart':
    case 'lampsEnd':
      return true;
  }

  return false;
}

const schema = {
  sendVisitMessage: SB.object({
    visitorSpecies: SB.tuple(Pet.ruleset.petSpeciesTuple),
    visitorName: SB.string().optional(),
    visitorPhoto: SB.string().optional(),
    visitorId: SB.string(),
    day: SB.number(),
  }),
  sendDailyBonusMessage: SB.object({ subFeature: SB.string() }),
  sendBattleRoyaleEndingMessage: SB.object({ subFeature: SB.string() }),
  sendLiveOpsMessage: SB.object({
    day: SB.number(),
    type: SB.tuple(liveOpsEventType),
  }),
  sendRetentionMessage: SB.object({}),
  sendWeeklyLeaderboardMessage: SB.object({
    type: SB.tuple(['28h', '4h']),
  }),
  sendGuestMessage: SB.object({
    notificationId: SB.string(),
  }),
  sendMiniappWelcome: SB.object({}),
};

export default {
  schema,
  actions: extractScheduledActionHandlers(
    createScheduledActions(schema)({
      sendVisitMessage: (state, args, api) => {
        const userId = api.getUserID();
        const species = getEquippedPetSpecies(state) ?? 'ball';
        const now = api.date.now();
        const payload = {
          ...generateReplicantChatbotPayload({
            feature: Feature.VISIT._,
            $subFeature: `visit_non_friend_d${args.day}`,
          }),
          friendId: userId,
          friendPetID: species,
          friendPetStomach: getAttributeValue(state, 'stomach', now),
          friendPetLove: getAttributeValue(state, 'love', now),
          friendDirtiness: getAttributeValue(state, 'dirtiness', now),
          friendUrine: getAttributeValue(state, 'urine', now),
          friendStress: Pet.getters.getStressValue(state, now),
          friendToothAcidity: getAttributeValue(state, 'toothAcidity', now),
          friendAge: Pet.getters.getPetAge(state, now),
          // Inverse since we're sending the message locally.
          visits: getVisitedCount(state, args.visitorId),
          visited: getVisitsCount(state, args.visitorId),
        };

        const message = getChatbotFlexMessage({
          template: 'visited',
          payload,
          data: {
            locale: getLocale(state),
            petName: state.pet.name,
            species,
            visitor: {
              name: args.visitorName ?? 'Unknown',
              photo: args.visitorPhoto ?? GENERIC_AVATAR,
              pet: args.visitorSpecies,
            },
          },
        });

        deliverChatbotMessage(state, api, {
          receiverId: userId,
          message,
          channel: 'oa',
        });

        Fate.modifiers.resolveStatusEffect(state, 'hadVisitor', true, now);

        state.visit.lastVisitorName = args.visitorName;
      },

      sendDailyBonusMessage: (state, args, api) => {
        const payload = {
          ...generateReplicantChatbotPayload({
            feature: Feature.NOTIFICATION._,
            $subFeature: args.subFeature,
          }),
        };

        const message = getChatbotFlexMessage({
          template: 'dailyBonusMessage',
          payload,
          data: {
            locale: getLocale(state),
          },
        });

        deliverChatbotMessage(state, api, {
          receiverId: api.getUserID(),
          message,
          channel: getChatbotMessageChannel(
            state,
            { name: 'liveOps' },
            api.date.now(),
          ),
        });
      },

      sendBattleRoyaleEndingMessage: (state, args, api) => {
        if (getStreakBattleRoyaleStatus(state) !== 'active') {
          return;
        }

        const payload = {
          ...generateReplicantChatbotPayload({
            feature: Feature.NOTIFICATION._,
            $subFeature: args.subFeature,
          }),
        };

        const message = getChatbotFlexMessage({
          template: 'liveOps',
          payload,
          data: {
            type: 'fuji',
            locale: getLocale(state),
          },
        });

        deliverChatbotMessage(state, api, {
          receiverId: api.getUserID(),
          message,
          channel: getChatbotMessageChannel(
            state,
            { name: 'liveOps' },
            api.date.now(),
          ),
        });
      },

      sendLiveOpsMessage: (state, args, api) => {
        const { day, type } = args;
        const now = api.date.now();

        // Find the next message to schedule
        const currentEventIndex = liveOpsSchedule.findIndex(
          (schedule) => schedule.day === day && schedule.type === type,
        );

        // Discard jobs for events removed from the schedule. Login schedules current events.
        if (currentEventIndex === -1) return;

        const nextEvent =
          liveOpsSchedule[(currentEventIndex + 1) % liveOpsSchedule.length];
        const weekday = nextEvent.day;
        const { hour, minute } = nextEvent.time;

        // Get a timestamp for the next event
        let nextEventTime = getJSTDateTime(now).set({ hour, minute, weekday });

        // It could be a Monday event on a Sunday which will move time back
        if (now > nextEventTime.valueOf()) {
          nextEventTime = nextEventTime.plus({ week: 1 });
        }

        // Schedule the next one
        api.scheduledActions.schedule.sendLiveOpsMessage({
          args: {
            day: nextEvent.day,
            type: nextEvent.type,
          },
          notificationId: 'liveOps',
          delayInMS:
            nextEventTime.valueOf() -
            now +
            getJitterDelayInMs(api.math.random, duration({ hours: 1 })),
        });

        // The unlock criteria for the feature may not be met
        if (!shouldSendLiveOpsMessage(state, type, now)) {
          return;
        }

        // Dream Drift uses push-only copy rather than the retired race artwork.
        if (type === 'dreamDrift') {
          deliverChatbotMessage(state, api, {
            receiverId: api.getUserID(),
            message: chatbotMessageTemplates.simplePushNotification({
              args: getLocaleConfig(getLocale(state)).guest.dreamDrift,
              payload: generateReplicantChatbotPayload({
                feature: Feature.NOTIFICATION._,
                $subFeature: 'notification_dreamDrift',
              }),
            }),
            // Preserve the existing live-ops mute before allowing push delivery.
            channel: getChatbotMessageChannel(state, { name: 'liveOps' }, now),
          });
          return;
        }

        let subType = undefined;
        // If it's a tournament message figure out the sub type
        if (
          type === 'tournamentEnd' ||
          type === 'tournamentStart' ||
          type === 'tournamentSoon' ||
          type === 'tournamentTomorrow'
        ) {
          // The "ended" message is sent as the new tournament begins
          // To get the correct one, move the time back 5 minutes just in case
          const safeNow =
            type === 'tournamentEnd' ? now - duration({ minutes: 5 }) : now;
          const tournament = match2TournamentGetters.getTournamentPeriod(
            state,
            safeNow,
          );

          // Shouldn't not have a tournament, but just in case
          const backupTournamentType =
            state.match2.level < match2TournamentRuleset.flyingHighUnlockLevel
              ? 'star'
              : 'streak';

          subType = tournament?.type ?? backupTournamentType;
        }

        const payload = generateReplicantChatbotPayload({
          feature: Feature.NOTIFICATION._,
          $subFeature: `${Feature.NOTIFICATION._}_${type}`,
        });

        const message = getChatbotFlexMessage({
          template: 'liveOps',
          payload,
          data: {
            type,
            subType,
            locale: getLocale(state),
          },
        });

        // Deliver the current message
        deliverChatbotMessage(state, api, {
          receiverId: api.getUserID(),
          message,
          channel: getChatbotMessageChannel(state, { name: 'liveOps' }, now),
        });
      },

      sendRetentionMessage: (state, args, api) => {
        const payload = {
          ...generateReplicantChatbotPayload({
            feature: Feature.NOTIFICATION._,
            $subFeature: Feature.NOTIFICATION.RETENTION_D3,
          }),
        };

        const species = getEquippedPetSpecies(state) ?? 'ball';

        const message = getChatbotFlexMessage({
          template: 'retentionMessage',
          payload,
          data: {
            locale: getLocale(state),
            species,
          },
        });

        deliverChatbotMessage(state, api, {
          receiverId: state.id,
          message,
          channel: areGuestNotificationsEnabled(state) ? 'push' : 'oa',
        });
      },

      sendWeeklyLeaderboardMessage: (state, args, api) => {
        // No event running, bail. Can happen if AB test was disabled.
        if (!getOngoingWeeklyLeaderboardEvent(state, api.date.now())) {
          return;
        }

        // Schedule the next message.
        scheduleWeeklyLeaderboardMessages(state, api);

        // Deliver the message.
        const notificationText = getLocaleConfig(getLocale(state))
          .weeklyLeaderboard[args.type];
        deliverChatbotMessage(state, api, {
          receiverId: state.id,
          message: chatbotMessageTemplates.simplePushNotification({
            args: notificationText,
            payload: generateReplicantChatbotPayload({
              feature: Feature.NOTIFICATION._,
              $subFeature: `${Feature.NOTIFICATION._}_weekly_leaderboards_${args.type}`,
            }),
          }),
          channel: 'push', // Force push notification.
        });
      },

      sendGuestMessage: (state, args, api) => {
        // Retire queued Turtle Race jobs without reporting invalid-message errors.
        if (args.notificationId === 'turtleRace') return;
        // Check if the argument is a valid guest notification ID
        if (!isValidGuestNotificationId(args.notificationId)) {
          api.reportError(
            new Error(`Invalid guest notification ID: ${args.notificationId}`),
          );
          return;
        }

        // Check if guest notifications are enabled and just ignore if not
        if (!areGuestNotificationsEnabled(state)) {
          return;
        }

        // Otherwise, send the guest push notification
        sendGuestPN(state, api, {
          itemId: args.notificationId as GuestNotificationId,
        });
      },

      sendMiniappWelcome: (state, args, api) => {
        // Define the payload for the chatbot message
        const payload = generateReplicantChatbotPayload({
          feature: 'miniapp',
          $subFeature: 'miniapp_migration_bump',
        });

        // Create the chatbot message using the invite template
        const message = getChatbotFlexMessage({
          template: 'miniappWelcome',
          payload,
          data: {
            locale: getLocale(state),
          },
          analyticsUserProperties: {},
        });

        // Deliver the chatbot message
        deliverChatbotMessage(state, api, {
          receiverId: state.id,
          message,
          channel: 'oa',
        });
      },
    }),
  ),
};
